import {defineStore} from "pinia";
import {CampaignService} from "@/javascript/services/campaign.service";
import {FundService} from "@/javascript/services/fund.service";
import {ReportService} from "@/javascript/services/report.service";

export const campaignStatusActive = "Active";
export const campaignStatusUpcoming = "Upcoming";
export const campaignStatusEnded = "Ended";

export const useCampaignStore = defineStore('campaign', {
  state: () => ({
    // campaignService: new CampaignService(),
    reportService: new ReportService(),
    campaignService: null,
    fundService: null,
    organizationId: null,
    modelName: "Campaign",
    // these are for analytic events
    traceableType: "Campaign",
    traceableName: "campaign",
    traceableAssociation: "Donation",

    loading: true,
    campaigns: [],
    funds: [],
    donations: [],
    currentRecord: {},
    loadingRecord: true,
    showNewModal: false,
    donationDataLoaded: false,
    periodDonationSum: 0,
    periodDonationCount: 0,
    paymentDataLoaded: false,
    donationDataDateRange: "",
    donationTypeData: {},
    donationTypeDataLoaded: false,
    topDonors: [],
    topDonorsLoaded: false,
    processorTypeData: {}, // Chart data for online / offline donations
    processorTypeDataLoaded: false,
    donationsByFundData: {}, // Chart data for fund breakdown of donations
    donationsByFundDataLoaded: false,
    currentInterval: "weekly",
    meta: {
      page: 1,
      pages: 1
    },
    filter: {
      sort: {
        key: "created_at",
        order: "asc"
      },
      search: {
        key: "name_or_fund_name_cont", // Ransack search query, add more here to expand the search results
        term: null
      }
    },
    errors: null
  }),

  actions: {
    /**
     * This sets the organizationId which is required to load the endpoint service correctly
     * @param id
     */
    setOrganizationId(id) {
      if (id) {
        this.organizationId = id;
        this.campaignService = new CampaignService(id);
        this.fundService = new FundService(id);
      } else {
        throw `Organization ID is not set: ${id}`;
      }
    },

    clearData() {
      this.topDonors = [];
      this.topDonorsLoaded = false;
      this.donationTypeData = {};
      this.donationTypeDataLoaded = false;
      this.processorTypeData = {};
      this.processorTypeDataLoaded = false;
      this.donationsByFundData = {};
      this.donationsByFundDataLoaded = false;
      this.donations = [];

      // this.periodDonationSum = 0;
      // this.periodDonationCount = 0;
    },

    // async loadData() {
    //   try {
    //
    //     await this.getCampaigns();
    //     await this.getFunds();
    //
    //     await this.loadDonationData();
    //     await this.loadPaymentData();
    //     // await this.loadTopDonors();
    //     await this.loadDonationTypeData();
    //     await this.loadProcessorTypeData();
    //     await this.loadDonationsByFundData();
    //   } catch (e) {
    //     console.error("Error loading data", e);
    //   }
    // },

    loadData() {
      this.getCampaigns();
      this.getFunds();

      this.loadDonationData();
      this.loadPaymentData();
      // await this.loadTopDonors();
      this.loadDonationTypeData();
      this.loadProcessorTypeData();
      this.loadDonationsByFundData();
    },

    loadDataWithIntervals() {
      // Loads the data that is dependent on intervals so we don't download the campaigns and funds repeatedly
      this.paymentDataLoaded = false;
      this.donationTypeDataLoaded = false;
      this.processorTypeDataLoaded = false;
      this.donationsByFundDataLoaded = false;

      this.loadDonationData();
      this.loadPaymentData();
      // await this.loadTopDonors();
      this.loadDonationTypeData();
      this.loadProcessorTypeData();
      this.loadDonationsByFundData();
    },

    loadPaymentData() {
      if (!this.currentRecord?.id) return;
      return this.campaignService.giftPayments(this.currentRecord.id, this.currentInterval)
        .then(res => {
          if (res) {
            this.paymentData = res;
            this.paymentDataLoaded = true;
          }
        }).catch((e) => {
          console.error('error loading payment data', e.toString());

        });
    },

    loadDonationData() {
      if (!this.currentRecord) return Promise.resolve();
      this.donationDataLoaded = false;

      return this.campaignService.gifts(this.currentRecord.id, this.currentInterval)
        .then(donationData => {
          this.donationData = donationData;
          this.donationDataDateRange = donationData.date_range;
          this.periodDonationSum = donationData.donations_sum;
          this.periodDonationCount = donationData.donations_count;
          this.donationDataLoaded = true;
        })
        .catch(e => {
          console.error('error loading donation data', e.toString());
          this.donationDataLoaded = true; // Set to true even on error to indicate loading is complete
        });
    },

    loadTopDonors() {
      this.topDonors = [];
      this.topDonorsLoaded = false;

      if (this.currentRecord && this.currentRecord.id) {
        return this.campaignService.topDonors(this.currentRecord.id, this.currentInterval, 5)
          .then(response => {
            this.topDonors = Array.isArray(response) ? response : response.data;
            this.topDonorsLoaded = true;
          })
          .catch(error => {
            console.error('Error loading top donors:', error);
            this.topDonorsLoaded = true; // Set to true even on error to indicate loading is complete
          });
      } else {
        this.topDonorsLoaded = true;
        return Promise.resolve(); // Return a resolved promise for consistency
      }
    },

    loadDonationTypeData() {
      this.donationTypeDataLoaded = false;
      if (!this.currentRecord?.id) {
        return Promise.resolve();
      }
      return this.campaignService.giftFrequencies(this.currentRecord.id, this.currentInterval)
        .then(response => {
          this.donationTypeData = response;
          this.donationTypeDataLoaded = true;
        });
    },

    loadProcessorTypeData() {
      this.processorTypeDataLoaded = false;

      if (!this.currentRecord?.id) {
        this.processorTypeDataLoaded = true;
        return Promise.resolve();
      }

      return this.campaignService.giftTypes(this.currentRecord.id, this.currentInterval)
        .then(processorTypeData => {
          this.processorTypeData = processorTypeData;
          this.processorTypeDataLoaded = true;
        })
        .catch(error => {
          console.error('Error loading processor types:', error);
          this.processorTypeDataLoaded = true; // Set to true even on error to indicate loading is complete
        });
    },

    loadDonationsByFundData() {
      this.donationsByFundDataLoaded = false;

      return this.reportService.donationsPerFund(this.organizationId, this.currentInterval)
        .then(donationsByFundData => {
          this.donationsByFundData = donationsByFundData;
          this.donationsByFundDataLoaded = true;
        })
        .catch(error => {
          console.error('Error loading donations by fund data:', error);
          this.donationsByFundDataLoaded = true; // Set to true even on error to indicate loading is complete
        });
    },

    /**
     * This will fetch the campaign, populate all the associated donation type data, processor type data, and related donations
     * @param id
     * @returns {Promise<*>}
     */
    getCampaign(id) {
      if (!id) return Promise.resolve();
      this.clearData(); // Clear stale data from the previous campaign
      this.loadingRecord = true;
      return this.campaignService.show(id)
        .then((res) => {
          this.currentRecord = res.record;
          this.permissions = res.permissions;
          return Promise.all([
            this.loadDonationTypeData(),
            this.loadProcessorTypeData(),
            this.campaignService.donations(id)
          ]);
        })
        .then(([_typeData, _processorData, donationsRes]) => {
          this.donations = donationsRes.data;
          this.loadingRecord = false;
        })
        .catch((e) => {
          console.error("cgc-1 : error loading fund", e);
          this.loadingRecord = false;
        });
    },


    getCampaigns() {
      // console.log('meta', this.meta);
      let params = new URLSearchParams();

      this.getFunds();

      return this.campaignService.paginate(this.meta.page, params, this.filter).then((res) => {
        this.campaigns = res.data;
        this.permissions = res.permissions;
        this.meta = res.meta;
        return true;
      }).catch((e) => {
        console.error("Error fetching campaigns", e);
      }).finally(() => {
        this.loading = false;
      });
    },

    createCampaign(newCampaign) {
      // translate donation target
      if (newCampaign.donation_target > 0) {
        newCampaign.donation_target *= 100;
      } else {
        newCampaign.donation_target = null;
      }
      let params = {
        ...newCampaign,
        "organization_id": this.organizationId
      };


      return this.campaignService.create(params).then((res) => {
        if (res && res.json && newCampaign.banner_image) {
          return this.updateBannerImage(res.json.id, newCampaign.banner_image);
        } else {
          return Promise.resolve();
        }
      }).then(_ => {
        setTimeout(() => {
          this.getCampaigns();
          this.showNewModal = false;
          return true;
        }, 1500);
      });
    },

    updateBannerImage(id, file) {
      return this.campaignService.uploadBannerImage(id, file);
    },

    updateCampaign(newParams) {
      this.loadingRecord = true;
      let params = {};
      params = {
        campaign: {
          ...newParams,
        }
      };

      return this.campaignService.update(newParams.id, params)
        .then(_ => {
          if (newParams.banner_image) {
            return this.updateBannerImage(newParams.id, newParams.banner_image);
          } else {
            return Promise.resolve();
          }
        }).then(_ => {
          this.getCampaigns();
          return this.getCampaign(newParams.id);
        }).catch((e) => {
          console.error("error updating campaign", e.toString());
        });
    },

    getFunds() {
      let params = new URLSearchParams();
      params.set('query[organization_id_eq]', this.organizationId);
      return this.fundService.index(params)
        .then(res => this.funds = res.data)
        .catch((e) => {
          console.error("Error fetching funds", e.toString());
        });
    },

    /**
     * Method used in generic Dashboard Show component to load the record.
     * @param id
     */
    getRecord(id) {
      return this.getCampaign(id);
    },
    /**
     * Method used in generic Dashboard Show component to update the record.
     * @param data
     */
    updateRecord(data) {
      return this.updateCampaign(data);
    },
    nextPage() {
      this.meta.page++;
      return this.getCampaigns();
    },
    prevPage() {
      this.meta.page--;
      return this.getCampaigns();
    },
    moveToPage(page) {
      this.meta.page = page;
      return this.getCampaigns();
    },
    /**
     * Validate the passed edited state.
     * @param local
     */
    validate(local) {
      this.errors = {};
      if (!local.name) {
        this.errors['name'] = "Name required";
      }
      if (!local.fund_id) {
        this.errors['fund_id'] = "Fund Required";
      }
      if (!local.start_date) {
        this.errors['start_date'] = "Start Required";
      }
    },

    removeBannerImage() {
      return this.campaignService.removeBannerImage(this.currentRecord.id)
        .then(res => {
          if (res && res.status === 200) {
            // Force reactivity by creating a new object
            this.currentRecord = {...this.currentRecord, banner_image_url: null};
            return true;
          } else {
            return Promise.resolve();
          }
        }).catch((e) => {
          console.error('error removing banner image', e.toString());
        });
    },

    parseChartData(past, projected, labels, priorYear) {
      const datasets = [
        {
          label: '\xa0' + labels[0] + '\xa0',
          data: past,
          borderColor: '#10be5a',
          backgroundColor: 'rgba(16, 190, 90, 0.2)',
          fill: true,
          tension: 0.2,
          borderWidth: 2,
          pointRadius: 3,
          hoverRadius: 6,
        },
        {
          label: '\xa0' + labels[1] + '\xa0',
          data: projected,
          borderColor: '#3a4473',
          backgroundColor: 'rgba(0, 189, 255, 0.2)',
          fill: true,
          tension: 0.2,
          borderWidth: 2,
          borderDash: [4, 4],
          pointRadius: 3,
          hoverRadius: 6,
        },
      ];

      if (priorYear && Object.values(priorYear).some(v => v !== null)) {
        datasets.push({
          label: '\xa0' + (labels[2] || 'Prior Year') + '\xa0',
          data: priorYear,
          borderColor: '#d1d5db',
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.2,
          borderWidth: 1.5,
          borderDash: [6, 3],
          pointRadius: 2,
          hoverRadius: 4,
        });
      }

      return { datasets };
    },

    // Sets the Ransack sort terms based on the passed key
    // This gets passed to index method to sort the paginated results
    sort(key) {
      if (this.filter.sort.key === key) {
        // If the passed key is the current key, flip the current sort order
        this.filter.sort.order = this.filter.sort.order === 'asc' ? 'desc' : 'asc';
      } else {
        // Otherwise set the new key and order by Ascending
        this.filter.sort.key = key;
        this.filter.sort.order = 'asc';
      }

      return this.getCampaigns();
    },

    search(term) {
      this.filter.search.term = term;
      this.meta.page = 1; //this makes it paginate from page 1 not just current page (resets pagination)
      return this.getCampaigns();
    },

  },

  getters: {
    donationProjectionChartData(state) {
      const donationData = state.donationData['donations'];
      const projectionData = state.donationData['projections'];
      const priorYear = state.donationData['prior_year'];
      return this.parseChartData(donationData, projectionData, ["Gifts", "Projected Gifts", "Last Year"], priorYear);
    },

    paymentChartData(state) {
      const paymentData = state.paymentData['payments'];
      const projectionData = state.paymentData['projections'];
      const priorYear = state.paymentData['prior_year'];
      return this.parseChartData(paymentData, projectionData, ["Dollars", "Projected Dollars", "Last Year"], priorYear);
    },

    getCurrentMemberDonations(state) {
      if (state.donations) {
        return state.donations;
      } else {
        return [];
      }
    },

    getCurrentActiveCampaigns(state) {
      return state.campaigns.filter((campaign) => campaign.status === campaignStatusActive);
    },
    /**
     * Method used in generic Dashboard Index component.
     * Returns an object containing the index table data.
     * @param state
     */
    getIndexTableData(state) {
      let labels = [
        {label: 'Name', key: 'name', sort: true},
        {label: 'Fund', key: 'fund_name', sort: false},
        {label: 'Gifts', key: 'total_contributions', sort: true},
        {label: 'Target', key: 'donation_target', format: 'money', sort: true},
        {label: 'Raised', key: 'all_time_contribution_total', format: 'money', sort: true},
        {label: 'Status', key: 'tags', format: 'tags', sort: false},
        {label: 'Start Date', key: 'start_date', format: 'date', sort: true},
        {label: 'End Date', key: 'end_date', format: 'date', sort: true},
      ];
      // Splice in the org field if the current org has any children.
      // So it only shows for multi level orgs
      if (state.organization?.child_ids?.length) {
        labels.splice(2, 0, {
          label: "Organization",
          key: 'organization_name',
          sort: true,
          ransackKey: "organization_name"
        });
      }
      if (state.campaigns.length > 0) {
        let campaigns = state.campaigns.map((campaign) => {
          campaign.tags = [];

          if (campaign.archived) {
            campaign.tags.push({style: 'tertiary', value: 'Inactive'});
          } else if (campaign.visible === false) {
            campaign.tags.push({style: 'hidden', value: 'Hidden'});
          } else {
            campaign.tags.push('Active');
          }

          return {
            ...campaign,
            fund_name: campaign.fund_name
          };
        });
        return {
          labels: labels,
          rows: campaigns
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },
    /**
     * Method used in generic Dashboard Show component.
     * Returns an object containing the record table data.
     * @param state
     */
    getShowTableData(state) {
      let labels = [
        'label',
        'data',
      ];
      if (state.currentRecord && Object.hasOwn(state.currentRecord, 'id')) {
        let rows = [
          {
            label: 'Name',
            data: state.currentRecord.name
          },
          {
            label: 'Fund',
            data: state.currentRecord.fund_name
          },
          {
            label: 'Short Description',
            data: state.currentRecord.short_description
          },
          {
            label: 'Long Description',
            data: state.currentRecord.description
          },
          {
            label: 'Start Date',
            data: {
              format: 'date',
              value: state.currentRecord.start_date
            }
          },
          {
            label: 'End Date',
            data: {
              format: 'date',
              value: state.currentRecord.end_date
            }
          },
          {
            label: "Donation Suggestions",
            data: {
              format: 'boolean',
              value: state.currentRecord.donation_suggestions_enabled,
              trueValue: "Enabled",
              falseValue: "Disabled"
            }
          },
          {
            label: "Suggested Donations",
            data: {
              format: 'array',
              value: state.currentRecord.donation_suggestions,
              display: function (val) {
                return "$" + val.toString();
              } // Display Formatting Closure
            }
          },
          {
            label: 'Donation Display Style',
            data: state.currentRecord.donation_display_style === 'bar'
              ? 'Progress Bar'
              : state.currentRecord.donation_display_style === 'amount'
                ? 'Amount Raised'
                : 'Show Nothing'
          },
          {
            label: "Donor Wall",
            data: {
              format: 'boolean',
              value: state.currentRecord.donor_wall_enabled,
              trueValue: "Enabled",
              falseValue: "Disabled"
            }
          },
          {
            label: 'Target Amount',
            data: {
              format: 'money',
              value: state.currentRecord.donation_target
            }
          },
          {
            label: 'Total Gifts',
            data: state.currentRecord.total_contributions,
            tooltip: 'Updates every few minutes'
          },
          {
            label: 'Total Dollars',
            data: {
              format: 'money',
              value: state.currentRecord.all_time_contribution_total
            },
            tooltip: 'Updates every few minutes'
          },
          {
            label: 'Latest Gift',
            data: {
              format: 'date',
              value: (state.currentRecord.last_donation) ? state.currentRecord.last_donation.created_at : ''
            }
          },
          {
            label: 'Latest Gift Amount',
            data: {
              format: 'money',
              value: (state.currentRecord.last_donation) ? state.currentRecord.last_donation.amount : ''
            }
          },

          {
            label: 'Banner Image',
            data: {
              format: 'image',
              value: state.currentRecord.banner_image_url
            }
          },
          {
            label: "Visibility",
            data: {
              format: 'boolean',
              value: state.currentRecord.visible,
              trueValue: "Visible",
              falseValue: "Hidden"
            }
          },
        ];
        return {
          labels: labels,
          rows: rows
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },
    /**
     * Method used in generic Dashboard Show component.
     * Returns an object with each key being the dashboard panel name of an association,
     *  and each value being the table data for that association.
     */
    getAssociationTableData(state) {
      if (!this.permissions?.canCreate) {
        return [];
      }
      let labels = [
        {label: 'Name', key: 'name'},
        {label: 'Email', key: 'email'},
        {label: 'Amount', key: 'amount', format: 'money'},
        {label: 'Status', key: 'status'},
        {label: 'Date of Gift', key: 'paid_at', format: 'date'}
      ];

      if(state.currentRecord && Object.hasOwn(state.currentRecord, 'id')) {
        let rows = state.getCurrentMemberDonations.map((donation) => {
          donation.name = `${donation.user.first_name} ${donation.user.last_name}`;
          donation.email = donation.user.email;
          donation.status = String(donation.status).charAt(0).toUpperCase() + String(donation.status).slice(1);
          return donation;
        });
        return {
          gifts: {
            labels: labels,
            rows: rows
          }
        };
      } else {
        return {
          gifts: {
            labels: labels,
            rows: []
          }
        };
      }
    },
    /**
     * Method used in generic Dashboard Show component.
     * Returns the data needed for the editing form table.
     * @param state
     */
    getEditingTableData(state) {
      if (state.currentRecord && Object.hasOwn(state.currentRecord, 'id')) {
        return [
          {label: 'Name', key: 'name', input: 'text'},
          {
            label: 'Fund',
            key: 'fund_id',
            input: 'select',
            options: state.funds.map((fund) => {
              return {
                label: fund.name,
                value: fund.id
              };
            })
          },
          {label: 'Short Description', key: 'short_description', input: 'text'},
          {label: 'Long Description', key: 'description', input: 'textarea'},
          {label: 'Start Date', key: 'start_date', input: 'date', dependant: 'end_date'},
          {label: 'End Date', key: 'end_date', input: 'date', min_date: 'start_date'},
          {label: 'Donation Suggestions Enabled', key: 'donation_suggestions_enabled', input: 'checkbox'},
          {
            label: 'Donation Suggestions',
            helperText: "Maximum of 4 donation suggestions recommended.",
            key: 'donation_suggestions',
            input: 'chips',
            inputType: 'number',
            displayValue: function (val) {
              return "$" + val.toString();
            } // Display Formatting Closure
          },

          {
            label: 'Donation Display Style',
            key: 'donation_display_style',
            input: 'select',
            options: [
              {label: 'Show Nothing', value: 'none'},
              {label: 'Progress Bar', value: 'bar'},
              {label: 'Amount Raised', value: 'amount'}
            ]
          },

          {label: 'Donor Wall Enabled', key: 'donor_wall_enabled', input: 'checkbox'},
          {label: 'Target Amount', key: 'donation_target', input: 'money'},
          {label: 'Banner Image', key: 'banner_image', input: 'image', removeButton: true},
          {label: 'Visible', key: 'visible', input: 'checkbox'}
        ];
      } else {
        return [];
      }
    },
  }
});
